<!-- =========================================================================================
  File Name: ClusteringMap.vue
  Description: Main clustering map component with Google Maps and deck.gl integration
  ----------------------------------------------------------------------------------------
  Item Name: Serviroutes - Clustering Module
  Author: Serviroutes Team
  Version: 2025-10-23
========================================================================================== -->

<template>
	<div id="clustering-map-container">
		<!-- Loading Overlay -->
		<transition name="fade">
			<div v-if="isLoading" class="loading-overlay flex flex-col items-center justify-center">
				<vs-progress indeterminate color="primary"></vs-progress>
				<p class="text-center text-black mt-3">{{ $t("Loading") }}...</p>
			</div>
		</transition>
		<!-- Map Container -->
		<div id="clustering-map" ref="mapContainer" class="clustering-map-wrapper"></div>

		<!-- View Mode Toggle Control -->
		<transition name="fade">
			<div v-if="hasClusters" class="view-mode-control">
				<div class="mode-option" :class="{active: viewMode === 'clusters'}" @click="setViewMode('clusters')">
					{{ $t("Clustering.Clusters") }}
				</div>
				<div
					class="mode-option"
					:class="{active: viewMode === 'micro_clusters'}"
					@click="setViewMode('micro_clusters')"
				>
					{{ $t("Clustering.MicroClusters") }}
				</div>
			</div>
		</transition>

		<!-- Backdrop for Speed Dial (closes on click) -->
		<transition name="fade">
			<div v-if="isFabExpanded" class="fab-backdrop" @click="isFabExpanded = false"></div>
		</transition>

		<!-- Speed Dial FAB (Expandable Action Menu) -->
		<div class="speed-dial-container">
			<!-- Expanded Action Buttons -->
			<transition-group name="fab-slide" tag="div" class="fab-actions">
				<!-- Restore Wizard Button (when minimized) -->
				<vs-button
					v-if="isFabExpanded && showWizard && isWizardMinimized"
					key="restore-wizard"
					class="fab-action pulse-animation"
					color="primary"
					type="gradient"
					:title="$t('Wizard.RestoreWizard')"
					@click="handleFabAction(restoreWizard)"
				>
					<div class="fab-action-content">
						<feather-icon icon="Edit3Icon" svgClasses="w-5 h-5" />
						<span class="fab-action-text">{{ $t("Wizard.ContinueCreating") }}</span>
						<span class="step-badge-small">{{ wizardCurrentStep }}/5</span>
					</div>
				</vs-button>

				<!-- View Statistics Panel Button (when panel is closed and clusters exist) -->
				<vs-button
					v-if="isFabExpanded && hasClusters && !showStatisticsPanel"
					key="view-statistics"
					class="fab-action"
					color="warning"
					type="gradient"
					:title="$t('Clustering.ViewStatistics')"
					@click="handleFabAction(openStatisticsPanel)"
				>
					<div class="fab-action-content">
						<feather-icon icon="BarChart2Icon" svgClasses="w-5 h-5" />
						<span class="fab-action-text">{{ $t("Clustering.ViewStatistics") }}</span>
					</div>
				</vs-button>

				<!-- Clear Map Button (when viewing clusters) -->
				<vs-button
					v-if="isFabExpanded && hasClusters"
					key="clear-map"
					class="fab-action"
					color="danger"
					type="gradient"
					:title="$t('Clustering.ClearMap')"
					@click="handleFabAction(clearSelection)"
				>
					<div class="fab-action-content">
						<feather-icon icon="Trash2Icon" svgClasses="w-5 h-5" />
						<span class="fab-action-text">{{ $t("Clustering.ClearMap") }}</span>
					</div>
				</vs-button>

				<!-- Toggle Heatmap Button (when there are points or clusters) -->
				<vs-button
					v-if="isFabExpanded && (hasSelectedPoints || hasClusters)"
					key="toggle-heatmap"
					class="fab-action"
					:color="showHeatmap ? 'success' : 'dark'"
					type="gradient"
					:title="showHeatmap ? $t('Clustering.DisableHeatmap') : $t('Clustering.EnableHeatmap')"
					@click="handleFabAction(toggleHeatmap)"
				>
					<div class="fab-action-content">
						<feather-icon :icon="showHeatmap ? 'EyeOffIcon' : 'TrendingUpIcon'" svgClasses="w-5 h-5" />
						<span class="fab-action-text">{{
							showHeatmap ? $t("Clustering.DisableHeatmap") : $t("Clustering.EnableHeatmap")
						}}</span>
					</div>
				</vs-button>

				<!-- View Cluster List Button -->
				<vs-button
					v-if="isFabExpanded && !showClusterList"
					key="view-clusters"
					class="fab-action"
					color="primary"
					type="gradient"
					:title="$t('Clustering.ClusterList')"
					@click="handleFabAction(toggleClusterList)"
				>
					<div class="fab-action-content">
						<feather-icon icon="ListIcon" svgClasses="w-5 h-5" />
						<span class="fab-action-text">{{ $t("Clustering.ClusterList") }}</span>
					</div>
				</vs-button>

				<!-- Create New Clustering Button -->
				<vs-button
					v-if="isFabExpanded && !showWizard"
					key="create-clustering"
					class="fab-action"
					color="success"
					type="gradient"
					:title="$t('Clustering.CreateNew')"
					@click="handleFabAction(openWizard)"
				>
					<div class="fab-action-content">
						<feather-icon icon="PlusCircleIcon" svgClasses="w-5 h-5" />
						<span class="fab-action-text">{{ $t("Clustering.CreateNew") }}</span>
					</div>
				</vs-button>
			</transition-group>

			<!-- Main FAB Toggle Button -->
			<vs-button
				class="main-fab"
				:class="{'fab-expanded': isFabExpanded}"
				color="primary"
				type="gradient"
				size="large"
				:title="isFabExpanded ? $t('Wizard.Close') : $t('Clustering.QuickActions')"
				@click="toggleFab"
			>
				<feather-icon :icon="isFabExpanded ? 'XIcon' : 'MenuIcon'" svgClasses="w-6 h-6" />
			</vs-button>
		</div>

		<!-- Clustering Wizard -->
		<transition name="slide-left">
			<clustering-wizard
				v-if="showWizard"
				v-show="!isWizardMinimized"
				ref="wizard"
				:map="map"
				:deck-overlay="deckOverlay"
				@close="closeWizard"
				@cancel="handleWizardCancel"
				@minimize="minimizeWizard"
				@generate-clustering="handleWizardGenerate"
				@load-points-to-map="handleLoadPointsToMap"
				@sync-points="handleSyncPoints"
				@start-polygon-drawing="startPolygonSelection"
				@finish-polygon-drawing="finishPolygonDrawing"
				@undo-last-polygon="undoLastPolygon"
				@clear-all-polygons="clearAllPolygons"
				@start-click-create="startClickCreateMode"
				@stop-click-create="stopClickCreateMode"
				@zoom-to-point="zoomToPoint"
				@export-clustering="handleExportClustering"
				@open-cluster-list="handleWizardViewList"
				@view-on-map="viewClusteringOnMap"
			/>
		</transition>
		<!-- Cluster List Sidebar -->
		<transition name="slide-right">
			<cluster-list
				v-if="showClusterList"
				:clusters="allClusters"
				:pagination="clustersPagination"
				:is-loading="isLoadingClusters"
				:current-view-mode="viewMode"
				@change-view-mode="handleViewModeChange"
				@view-clusters-on-map="viewClusteringOnMap"
				@delete="confirmDeleteClustering"
				@update-clustering="handleUpdateClustering"
				@close="toggleClusterList"
				@page-change="onClusterPageChange"
				@refresh-clusters="onRefreshClusters"
				@toggle-wizard="openWizard"
				@export-clustering="handleExportClustering"
			/>
		</transition>
		<!-- Enhanced Statistics Panel -->
		<cluster-statistics-panel
			v-if="hasClusters && showStatisticsPanel"
			:statistics="clusterStatistics"
			:selected-cluster-id="selectedCluster"
			:items-per-page="5"
			:focused-cluster="focusedCluster"
			@select-cluster="handleClusterSelect"
			@view-cluster="handleClusterView"
			@hover-cluster="handleClusterHover"
			@unhover-cluster="handleClusterUnhover"
			@focus-cluster="focusOnCluster"
			@clear-focus="clearFocus"
			@close="handleStatisticsPanelClose"
		/>

		<!-- Selected Points Panel -->
		<transition name="slide-up">
			<selected-points-panel
				v-if="showSelectedPointsPanel && hasLoadedPoints && !hasClusters"
				:selected-points="selectedPoints"
				:total-points="loadedPoints.length"
				:polygons-count="drawnPolygons.length"
				@close="toggleSelectedPointsPanel"
				@select-all="selectAllPoints"
				@deselect-all="deselectAllPoints"
				@clear-polygons="clearPolygonSelection"
				@point-click="zoomToPoint"
				@remove-point="removePointFromSelection"
			/>
		</transition>

		<!-- Toggle Selected Points Panel Button -->
		<vs-button
			v-if="hasLoadedPoints && !hasClusters && !showSelectedPointsPanel"
			class="toggle-selected-panel-btn"
			color="success"
			icon-pack="feather"
			icon="icon-check-circle"
			:title="$t('Clustering.ViewSelected')"
			@click="toggleSelectedPointsPanel"
		>
			<span class="badge">{{ selectedPoints.length }}</span>
		</vs-button>

		<!-- Delete Selected Polygon Button (Floating) -->
		<transition name="fade">
			<vs-button
				v-if="selectedPolygon"
				class="delete-polygon-btn"
				color="danger"
				icon-pack="feather"
				icon="icon-trash-2"
				:title="$t('Clustering.DeletePolygon')"
				@click="confirmRemovePolygon(selectedPolygon)"
			>
				{{ $t("Clustering.DeletePolygon") }}
			</vs-button>
		</transition>
		<!-- Cluster Info Window (Point Details & Cluster Summary) -->
		<transition name="fade">
			<cluster-info-window
				v-if="showInfoWindow && infoWindowData"
				:data="infoWindowData"
				:position="infoWindowPosition"
				:cluster-stats="getClusterStats(infoWindowData.clusterId)"
				:view-mode="viewMode"
				:clustering="currentClustering"
				:show-street-view="true"
				:is-focused="isFocused"
				@close="closeInfoWindow"
				@zoom-to-point="handleZoomToPointFromInfo"
				@zoom-to-cluster="handleZoomToClusterFromPolygon"
				@focus-on-cluster="handleFocusOnClusterFromPolygon"
				@clear-focus="clearFocus"
			/>
		</transition>
	</div>
</template>

<script>
import {clusteringMapMixin} from "@/components/clustering/model/ClusteringMapMixin.js";

export default {
	mixins: [clusteringMapMixin],
};
</script>

<style lang="scss" scoped>
#clustering-map-container {
	position: relative;
	height: 93vh;
	overflow: hidden;
	margin-top: -21px;
	margin-left: -19px;
	margin-right: -18px;
	margin-bottom: -19px;

	#clustering-map {
		width: 100%;
		height: 100%;
		min-height: 400px;
	}

	.loading-overlay {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: rgba(0, 0, 0, 0.5);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 1000;
	}

	// Backdrop for Speed Dial
	.fab-backdrop {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: rgba(0, 0, 0, 0.3);
		z-index: 52999;
		cursor: pointer;
	}

	// Speed Dial FAB Container
	.speed-dial-container {
		position: fixed;
		bottom: 45px;
		right: 90px;
		z-index: 53000;
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		gap: 12px;

		// Expandable actions container
		.fab-actions {
			display: flex;
			flex-direction: column;
			gap: 12px;
			align-items: flex-end;
		}

		// Individual action button
		.fab-action {
			padding: 14px 24px !important;
			border-radius: 50px;
			box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
			transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
			min-width: 220px;

			.fab-action-content {
				display: flex;
				align-items: center;
				gap: 12px;

				.fab-action-text {
					font-size: 0.9rem;
					font-weight: 600;
					white-space: nowrap;
				}

				.step-badge-small {
					background: rgba(255, 255, 255, 0.3);
					padding: 2px 8px;
					border-radius: 12px;
					font-size: 0.75rem;
					font-weight: 700;
					margin-left: auto;
				}
			}

			&:hover {
				transform: translateX(-8px) scale(1.05);
				box-shadow: 0 8px 28px rgba(0, 0, 0, 0.4);
			}

			&:active {
				transform: translateX(-4px) scale(1.02);
			}
		}

		// Main FAB Toggle Button
		.main-fab {
			width: 64px;
			height: 64px;
			border-radius: 50%;
			padding: 0 !important;
			box-shadow: 0 8px 32px rgba(var(--vs-primary), 0.5);
			transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
			display: flex;
			align-items: center;
			justify-content: center;

			&.fab-expanded {
				transform: rotate(90deg);
				background: linear-gradient(118deg, rgba(var(--vs-danger), 1), rgba(var(--vs-danger), 0.7)) !important;
			}

			&:hover {
				transform: translateY(-4px) scale(1.1);
				box-shadow: 0 12px 40px rgba(var(--vs-primary), 0.6);

				&.fab-expanded {
					transform: translateY(-4px) scale(1.1) rotate(90deg);
					box-shadow: 0 12px 40px rgba(var(--vs-danger), 0.6);
				}
			}

			&:active {
				transform: translateY(-2px) scale(1.05);

				&.fab-expanded {
					transform: translateY(-2px) scale(1.05) rotate(90deg);
				}
			}
		}
	}

	// Animation for fab actions (slide + fade)
	.fab-slide-enter-active,
	.fab-slide-leave-active {
		transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
	}

	.fab-slide-enter,
	.fab-slide-leave-to {
		opacity: 0;
		transform: translateX(100px) scale(0.5);
	}

	.fab-slide-move {
		transition: transform 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
	}

	.toggle-selected-panel-btn {
		position: absolute;
		bottom: 20px;
		left: 20px;
		z-index: 100;
		box-shadow: 0 4px 12px rgba(76, 175, 80, 0.4);
		padding: 12px 20px !important;
		cursor: pointer;
		transition: all 0.3s ease;
		display: flex;
		align-items: center;
		gap: 8px;

		.badge {
			background: white;
			color: #4caf50;
			padding: 2px 8px;
			border-radius: 12px;
			font-size: 0.75rem;
			font-weight: 700;
			min-width: 24px;
			text-align: center;
		}

		&:hover {
			transform: translateY(-4px);
			box-shadow: 0 6px 16px rgba(76, 175, 80, 0.5);
		}

		&:active {
			transform: translateY(-2px);
		}
	}

	// Delete polygon button (floating)
	.delete-polygon-btn {
		position: absolute;
		top: 20px;
		left: 50%;
		transform: translateX(-50%);
		z-index: 1000;
		box-shadow: 0 4px 16px rgba(244, 67, 54, 0.5);
		padding: 12px 24px !important;
		cursor: pointer;
		animation: bounceIn 0.5s ease;
		display: flex;
		align-items: center;
		gap: 8px;
		font-weight: 600;

		&:hover {
			transform: translateX(-50%) translateY(-4px);
			box-shadow: 0 6px 20px rgba(244, 67, 54, 0.6);
		}

		&:active {
			transform: translateX(-50%) translateY(-2px);
		}
	}

	@keyframes bounceIn {
		0% {
			transform: translateX(-50%) translateY(-100px);
			opacity: 0;
		}
		60% {
			transform: translateX(-50%) translateY(10px);
			opacity: 1;
		}
		80% {
			transform: translateX(-50%) translateY(-5px);
		}
		100% {
			transform: translateX(-50%) translateY(0);
		}
	}

	// Pulse animation for main FAB button
	@keyframes pulse {
		0% {
			box-shadow: 0 8px 32px rgba(var(--vs-primary), 0.5);
		}
		50% {
			box-shadow: 0 8px 32px rgba(var(--vs-primary), 0.8), 0 0 0 15px rgba(var(--vs-primary), 0);
		}
		100% {
			box-shadow: 0 8px 32px rgba(var(--vs-primary), 0.5);
		}
	}

	.pulse-animation {
		animation: pulse 2s infinite;
	}

	// Statistics panel styles are now in ClusterStatisticsPanel.vue component

	.action-buttons-container {
		position: absolute;
		top: 100px;
		right: 20px;
		z-index: 10;
		display: flex;
		flex-direction: column;
		gap: 8px;
	}
}

// Slide up transition for selected points panel
.slide-up-enter-active,
.slide-up-leave-active {
	transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
}

.slide-up-enter,
.slide-up-leave-to {
	transform: translateY(100%);
	opacity: 0;
}

// Transitions for wizard (slide from right)
.slide-left-enter-active {
	transition: transform 0.3s ease-out;
}

.slide-left-leave-active {
	transition: transform 0.3s ease-in;
}

.slide-left-enter {
	transform: translateX(100%);
}

.slide-left-leave-to {
	transform: translateX(100%);
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s ease-in-out;
}

.fade-enter,
.fade-leave-to {
	opacity: 0;
}

.slide-left-enter-active,
.slide-left-leave-active {
	transition: transform 0.3s ease-in-out;
}

.slide-left-enter,
.slide-left-leave-to {
	transform: translateX(100%);
}

.slide-right-enter-active,
.slide-right-leave-active {
	transition: transform 0.3s ease-in-out;
}

.slide-right-enter,
.slide-right-leave-to {
	transform: translateX(-100%);
}

// Responsive
@media screen and (max-width: 768px) {
	#clustering-map-container {
		height: calc(100vh - 60px);

		.action-buttons-container {
			top: 70px;
			right: 10px;
		}
	}
}
// View Mode Control
.view-mode-control {
	position: absolute;
	top: 20px;
	left: 50%;
	transform: translateX(-50%);
	z-index: 1000;
	background: white;
	border-radius: 30px;
	box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
	display: flex;
	padding: 4px;
	gap: 4px;

	.mode-option {
		padding: 8px 20px;
		border-radius: 25px;
		font-size: 0.9rem;
		font-weight: 600;
		cursor: pointer;
		transition: all 0.3s ease;
		color: #666;
		white-space: nowrap;

		&:hover {
			background: rgba(var(--vs-primary), 0.1);
			color: rgba(var(--vs-primary), 1);
		}

		&.active {
			background: rgba(var(--vs-primary), 1);
			color: white;
			box-shadow: 0 2px 8px rgba(var(--vs-primary), 0.4);
		}
	}
}
</style>
